import type { MessageInitShape } from '@bufbuild/protobuf'
import { type CallOptions, createClient } from '@connectrpc/connect'
import { createConnectQueryKey, useMutation } from '@connectrpc/connect-query'
import { useQueryClient, useMutation as useReactQueryMutation } from '@tanstack/react-query'

import { setArtifactMisclassification } from '@/gen/artifacts/store/v1/store_service-ArtifactStoreService_connectquery'
import { retryArtifact } from '@/gen/artifacts/store/v1/store_service-OrgArtifactStoreService_connectquery'
import {
  OrgArtifactStoreService,
  UploadArtifactRequestSchema,
} from '@/gen/artifacts/store/v1/store_service_pb'
import { ArtifactService } from '@/gen/inventory/v1/artifact_service_pb'

import { UPLOAD_ARTIFACT_TIMEOUT_MS } from '@/const/artifact-upload-constraints'

import { defaultTransport } from '@/lib/connect/connect-transport'

export const getListCompanyArtifactQueryKey = () => {
  return createConnectQueryKey({
    schema: ArtifactService.method.listCompanyArtifact,
    cardinality: 'finite',
    transport: defaultTransport,
  })
}

export const getListUserSourcedArtifactQueryKey = () => {
  return createConnectQueryKey({
    schema: ArtifactService.method.listUserSourcedArtifact,
    cardinality: 'finite',
    transport: defaultTransport,
  })
}

/** connect-query's useMutation takes no callOptions, hence the manual client. */
const uploadArtifactClient = createClient(OrgArtifactStoreService, defaultTransport)

export const useUploadArtifact = () => {
  const queryClient = useQueryClient()

  return useReactQueryMutation({
    mutationFn: (request: MessageInitShape<typeof UploadArtifactRequestSchema>) => {
      const callOptions: CallOptions = { timeoutMs: UPLOAD_ARTIFACT_TIMEOUT_MS }
      return uploadArtifactClient.uploadArtifact(request, callOptions)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListCompanyArtifactQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListUserSourcedArtifactQueryKey() })
    },
  })
}

export const useRetryArtifact = () => {
  const queryClient = useQueryClient()
  return useMutation(retryArtifact, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListUserSourcedArtifactQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListCompanyArtifactQueryKey() })
    },
  })
}

export const useSetArtifactMisclassification = () => {
  const queryClient = useQueryClient()
  return useMutation(setArtifactMisclassification, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListUserSourcedArtifactQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListCompanyArtifactQueryKey() })
    },
  })
}
